import { CarrierVisitsApi } from '@operations/app/api'
import { CarrierVisitRefreshEventPayload } from '@operations/app/api/signalRDtos/carrierVisitRefreshEventPayload'
import { CarrierVisitWorkInstructionUpsertedPayload } from '@operations/app/api/signalRDtos/carrierVisitWorkInstructionUpsertedPayload'
import { WorkInstructionDeletedEventPayload } from '@operations/app/api/signalRDtos/workInstructionDeletedEventPayload'
import { createApiClient } from '@operations/app/http-client'
import { EventTypes } from '@operations/messages/eventsTypes'
import { IEvent, MessageBus } from '@tom-ui/utils'
import { action, makeObservable, observable, runInAction } from 'mobx'

export class HasVisitWorkInstructionAndWorkQueueUIStore {
  vesselVisitId?: number
  hasWorkInstruction = false
  hasWorkQueue = false
  constructor(private readonly messageBus: MessageBus) {
    makeObservable(this, {
      vesselVisitId: observable,
      hasWorkInstruction: observable,
      hasWorkQueue: observable,
      setVesselVisitId: action,
    })

    this.messageBus.subscribeEvent<CarrierVisitWorkInstructionUpsertedPayload>(
      EventTypes.CarrierVisitWorkInstructionUpserted,
      this.handleWorkInstructionUpsertedEvent,
    )

    this.messageBus.subscribeEvent<WorkInstructionDeletedEventPayload>(
      EventTypes.WorkInstructionDeleted,
      this.handleWorkInstructionDeletedEvent,
    )

    this.messageBus.subscribeEvent<CarrierVisitRefreshEventPayload>(
      EventTypes.CarrierVisitRefresh,
      this.handleCarrierVisitRefreshEvent,
    )

    this.messageBus.subscribeEvent<number>(
      EventTypes.CraneSplitGeneratedForVisit,
      this.handleCraneSplitGeneratedForVisitEvent,
    )
  }

  public setVesselVisitId(vesselVisitId?: number) {
    this.vesselVisitId = vesselVisitId
  }

  private readonly handleWorkInstructionUpsertedEvent = async (
    event: IEvent<CarrierVisitWorkInstructionUpsertedPayload>,
  ) => {
    const payload = event.payload

    if (payload?.carrierVisitId === this.vesselVisitId && payload?.carrierVisitId) {
      await this.getVisitHasWorkInstructionAndWorkQueue(payload.carrierVisitId)
    }
  }

  private readonly handleWorkInstructionDeletedEvent = async (
    event: IEvent<WorkInstructionDeletedEventPayload>,
  ) => {
    const payload = event.payload

    if (this.vesselVisitId !== undefined && payload?.carrierVisitIds.includes(this.vesselVisitId)) {
      await this.getVisitHasWorkInstructionAndWorkQueue(this.vesselVisitId)
    }
  }

  private readonly handleCarrierVisitRefreshEvent = async (
    event: IEvent<CarrierVisitRefreshEventPayload>,
  ) => {
    const payload = event.payload

    if (payload?.carrierVisits?.some(cv => cv.carrierVisitId === this.vesselVisitId)) {
      await this.getVisitHasWorkInstructionAndWorkQueue(this.vesselVisitId!)
    }
  }

  private readonly handleCraneSplitGeneratedForVisitEvent = (event: IEvent<number>) => {
    const visitId = event.payload

    if (this.vesselVisitId === visitId) {
      runInAction(() => {
        this.hasWorkInstruction = true
        this.hasWorkQueue = true
      })
    }
  }

  public async load(carrierVisitId: number) {
    if (carrierVisitId === this.vesselVisitId) return
    this.setVesselVisitId(carrierVisitId)
    await this.getVisitHasWorkInstructionAndWorkQueue(carrierVisitId)
  }

  private async getVisitHasWorkInstructionAndWorkQueue(carrierVisitId: number) {
    const { data } =
      await createApiClient(CarrierVisitsApi).getHasWorkInstructionAndCraneWorkQueue(carrierVisitId)

    runInAction(() => {
      this.hasWorkInstruction = data.hasWorkInstruction
      this.hasWorkQueue = data.hasWorkQueue
    })
  }
}
